/**
 * Abstract interface for new messages notification storage.
 */
export interface NotificationStorage {
  getLastSeenCommentId(orderId: string): string | null;
  setLastSeenCommentId(orderId: string, commentId: string): void;
}

const STORAGE_KEY = 'itham-order-last-seen';
const MAX_AGE_DAYS = 45;

/**
 * localStorage-based implementation of NotificationStorage.
 * Stores a map of { orderId: { commentId, accessedAt } } and auto-cleans entries older than 45 days.
 */
export class LocalStorageNotificationStorage implements NotificationStorage {
  constructor() {
    this.cleanup();
  }

  getLastSeenCommentId(orderId: string): string | null {
    const data = this.readAll();
    return data[orderId]?.commentId ?? null;
  }

  setLastSeenCommentId(orderId: string, commentId: string): void {
    const data = this.readAll();
    data[orderId] = { commentId, accessedAt: new Date().toISOString() };
    this.safeWrite(data);
  }

  private readAll(): Record<string, { commentId: string; accessedAt: string }> {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    } catch {
      return {};
    }
  }

  private safeWrite(data: Record<string, { commentId: string; accessedAt: string }>): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // localStorage unavailable or quota exceeded – silently degrade
    }
  }

  private cleanup(): void {
    const data = this.readAll();
    const cutoff = Date.now() - MAX_AGE_DAYS * 24 * 60 * 60 * 1000;
    const cleaned: Record<string, { commentId: string; accessedAt: string }> = {};

    for (const [key, value] of Object.entries(data)) {
      if (new Date(value.accessedAt).getTime() > cutoff) {
        cleaned[key] = value;
      }
    }

    this.safeWrite(cleaned);
  }
}

export const notificationStorage: NotificationStorage = new LocalStorageNotificationStorage();
